import React, { useCallback, useState } from 'react';

import {
  OdProtosWebData_RecordedFeature as RecordedFeature,
  OdProtosWebData_VirtualAssessmentRecordingInput,
} from '__generated__/globalTypes';
import { Input, Text, Divider, Button, InputProps } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';
import isUrl from 'is-url';
import { useUpdateVirtualAssessmentRecordings } from 'sellDirect/graphql/useUpdateVirtualAssessmentRecordings';
import { useVirtualAssessmentData } from 'sellDirect/graphql/useVirtualAssessmentData';

interface ILinkUploadModuleProps {
  recordedFeature: RecordedFeature;
}

const FEATURES_WITH_LINK_UPLOAD = [RecordedFeature.WHOLE_HOME];

/**
 * Component for submitting a link for SSVA instead of a file upload.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const LinkUploadModule: React.FC<ILinkUploadModuleProps> = ({ recordedFeature }) => {
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [linkInvalidReason, setLinkInvalidReason] = useState<string>();
  const { updateSSVARecordings } = useUpdateVirtualAssessmentRecordings();

  const { refetch } = useVirtualAssessmentData();
  const { trackEvent } = useObservability();
  const [loading, setLoading] = useState(false);

  const appendRecordings = useCallback(
    (feature: RecordedFeature) => {
      const newRecordings: Array<OdProtosWebData_VirtualAssessmentRecordingInput> = [
        {
          fileName: linkUrl,
          recordedFeature: feature,
          s3Directory: linkUrl,
        },
      ];
      trackEvent('self-schedule', 'uploaded-new-urls');
      updateSSVARecordings(newRecordings);
    },
    [updateSSVARecordings, trackEvent, linkUrl],
  );

  const handleSubmit = useCallback(() => {
    const { isValid, invalidReason } = linkForUploadValidity(linkUrl);
    setLinkInvalidReason(invalidReason);
    if (isValid) {
      appendRecordings(recordedFeature);
      setLoading(true);
      /** Refetches data after submitting link */
      const refetchData = async () => {
        await refetch();
        setLoading(false);
      };
      refetchData();
    }
  }, [linkUrl, appendRecordings, refetch, recordedFeature]);

  const canSubmit = !!linkUrl.length;

  if (!FEATURES_WITH_LINK_UPLOAD.includes(recordedFeature)) {
    return null;
  }

  const onLinkChange: InputProps['onChange'] = (_event, newLink): void => {
    setLinkInvalidReason('');
    setLinkUrl(newLink ?? '');
  };
  return (
    <>
      <Divider my={7} />
      <Input
        analyticsName="cake-link-upload-module-video-link"
        my={5}
        id="url-input-1"
        type="text"
        aria-labelledby="video-link"
        placeholder="Paste your video link here"
        value={linkUrl}
        onChange={onLinkChange}
        unboundedWidth
        borderRadius="roundedSquare"
        label={
          <Text size="s1" fontWeight="semibold" color="neutrals100" id="video-link">
            Or submit a video link
          </Text>
        }
      />
      {linkInvalidReason && (
        <Text size="s1" mb={5} fontWeight="semibold" color="red50">
          {linkInvalidReason}
        </Text>
      )}
      <Button
        aria-label="Submit video link for virtual assessment to be done"
        analyticsName="cake-ssva-url-done-button"
        loading={loading}
        onClick={handleSubmit}
        disabled={!canSubmit || loading}
      >
        Done
      </Button>
    </>
  );
};

type UrlValidity = {
  isValid: boolean;
  invalidReason?: string;
};

const INVALID_URL_HTTPS_ERR_MSG = 'Invalid link, please enter a valid link starting with "https".';
const INVALID_URL_DOMAIN_ERR_MSG =
  'Invalid domain, please use one of the valid video hosting sites listed above.';

const INVALID_URL_GENERIC_ERR_MSG = 'Invalid link, please enter a valid link.';

const ALLOWED_LINK_DOMAINS = [
  'drive.google.com',
  'dropbox.com',
  'home.mycloud.com',
  'linksharing.samsungcloud.com',
  'my.matterport.com',
  'onedrive.live.com',
  'photos.app.goo.gl',
  'photos.google.com',
  'player.vimeo.com',
  'share.icloud.com',
  'vimeo.com',
  'www.dropbox.com',
  'www.icloud.com',
  'www.insidemaps.com',
  'www.loom.com',
  'www.youtube.com',
  'youtu.be',
  'youtube.com',
];

/** TODO: Add documentation */
export const linkForUploadValidity = (urlString: string): UrlValidity => {
  const isHttps = urlString.startsWith('https');
  const isValidUrl = isUrl(urlString);

  if (!isHttps) {
    return {
      isValid: false,
      invalidReason: INVALID_URL_HTTPS_ERR_MSG,
    };
  }

  if (!isValidUrl) {
    return {
      isValid: false,
      invalidReason: INVALID_URL_GENERIC_ERR_MSG,
    };
  }

  try {
    const url = new URL(urlString);
    if (!ALLOWED_LINK_DOMAINS.includes(url.hostname)) {
      return {
        isValid: false,
        invalidReason: INVALID_URL_DOMAIN_ERR_MSG,
      };
    }
  } catch {
    return {
      isValid: false,
      invalidReason: INVALID_URL_GENERIC_ERR_MSG,
    };
  }
  return { isValid: true };
};
